import { StepThankYou } from './steps/StepThankYou';
import { StepIntroVideo } from './steps/StepIntroVideo';
import { StepHowWeHelp } from './steps/StepHowWeHelp';
import { StepTargetAudience } from './steps/StepTargetAudience';
import { useSearchParams } from 'react-router-dom';
import { OnboardingStep } from './types';
import { IconOnboardingChevronLeft, IconOnboardingClose } from '../Icon';
import { StepInvite } from './steps/StepInvite';
import { Button } from '../buttons/Button';
import { useFetchSubCategory } from '../../lib/fetches';
import Skeleton from '../Skeleton';

export interface OnboardingProps {
    onComplete?: () => void;
    initialStep?: string;
}

export function Onboarding({
    onComplete,
    initialStep = 'thank-you',
}: OnboardingProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const currentStep = (searchParams.get('step') ||
        initialStep) as OnboardingStep;

    const backTransitions: Map<OnboardingStep, OnboardingStep> = new Map([
        ['invite', 'thank-you'],
        ['intro-video', 'thank-you'],
        ['how-we-help', 'intro-video'],
        ['target-audience', 'how-we-help'],
    ]);

    const handleNext = (step: OnboardingStep | '_complete') => {
        if (step === '_complete') {
            // @ts-ignore
            window.dataLayer?.push({
                event: 'onboarding_exit',
                screen_detail: currentStep,
            });

            onComplete?.();
        } else {
            searchParams.set('step', step);
            setSearchParams(searchParams);
        }
    };

    const handleBack = (step: OnboardingStep) => {
        searchParams.set('step', step);
        setSearchParams(searchParams);
    };

    const { data: category } = useFetchSubCategory('onboarding');

    if (!category) {
        return <Skeleton className="h-screen w-screen" />;
    }

    const renderStep = () => {
        switch (currentStep) {
            case 'thank-you':
                return <StepThankYou onNext={handleNext} category={category} />;

            case 'invite':
                return <StepInvite onNext={handleNext} category={category} />;

            case 'intro-video':
                return (
                    <StepIntroVideo onNext={handleNext} category={category} />
                );

            case 'how-we-help':
                return (
                    <StepHowWeHelp onNext={handleNext} category={category} />
                );

            case 'target-audience':
                return (
                    <StepTargetAudience
                        onNext={handleNext}
                        category={category}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className="inset-0 bg-white">
            <main className="mx-auto flex max-w-[892px] flex-col items-center justify-between gap-4 px-2 my-8 max-sm:px-4 md:my-12">
                {renderStep()}
            </main>

            {currentStep !== initialStep &&
                backTransitions.has(currentStep) && (
                    <div className="absolute left-8 top-8 cursor-pointer">
                        <Button
                            color="opaque"
                            onClick={() =>
                                handleBack(backTransitions.get(currentStep)!)
                            }
                        >
                            <IconOnboardingChevronLeft
                                className="text-ink-800"
                                size={24}
                                viewBox="0 0 40 40"
                            />
                        </Button>
                    </div>
                )}
            <div className="absolute right-8 top-8 cursor-pointer">
                <Button color="opaque" onClick={() => handleNext('_complete')}>
                    <IconOnboardingClose
                        className="text-ink-800"
                        size={24}
                        viewBox="0 0 40 40"
                    />
                </Button>
            </div>
        </div>
    );
}
